'use client';
import * as React from 'react';
import { useSearchParams } from 'next/navigation';
import { themes } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Theme } from '@mentimeter/ragnar-ui/themes';
import { type AbstractIntlMessages } from 'next-intl';
import { NextIntlParticipatingProvider } from '@mentimeter/i18n-participating/participating-provider';
import { DeviceContext } from '@mentimeter/ragnar-device';
import { usePubSub } from '../realtime/usePubSub';
import { useAppSelector } from '../redux-hooks';
import {
  getDSCWithTheme,
  languageSelector,
  paceModeSelector,
  participationIdentityModeSelector,
  voteKeySelector,
} from '../selectors';
import { gaTrackEvent, useTrack } from '../utils/tracker';
import { useAudienceConnect } from '../realtime/useAudienceConnect';
import { useAudienceConnectStore } from '../realtime/useAudienceConnectStore';
import type { ParticipantIdentity } from '../types';
import { AudiencePace } from './audience-pace/AudiencePace';
import { PresenterPace } from './presenter-pace/PresenterPace';
import { IdentificationContainer } from './identified-responses/IdentificationContainer';
import { getJoinedPresTrackProps } from './utils/getJoinedPresTrackProps';
import { useIdentity } from './identified-responses/useIdentity';
import { AuthenticationContainer } from './auth/AuthenticationContainer';
import { useAuthenticationStore } from './auth/authenticationStore';
import { AudienceLimitReachedModal } from './AudienceLimitReachedModal';

const THEME_OVERRIDE_STATUSES: Array<ParticipantIdentity['status']> = [
  'loading',
  'error',
  'needs_identification',
];

export const Series = ({
  nextIntlMessages,
}: {
  nextIntlMessages: AbstractIntlMessages;
}) => {
  const trackVoter = useTrack();
  const searchParams = useSearchParams();
  const source = searchParams.get('source');
  const verificationMethod = searchParams.get('verificationMethod');
  const voteKey = useAppSelector(voteKeySelector);
  const paceMode = useAppSelector(paceModeSelector);
  const { initialized } = React.useContext(DeviceContext);

  useAudienceConnect({
    readyToConnect: initialized,
  });
  const status = useAudienceConnectStore((state) => state.status);
  usePubSub(status === 'connected');

  const presentationTheme = useAppSelector(getDSCWithTheme);
  const language = useAppSelector(languageSelector);
  const participationIdentityMode = useAppSelector(
    participationIdentityModeSelector,
  );

  const { authenticationNeeded } = useAuthenticationStore();
  const { identity } = useIdentity();
  const theme =
    THEME_OVERRIDE_STATUSES.includes(identity.status) || authenticationNeeded
      ? themes.light
      : presentationTheme;

  React.useEffect(() => {
    gaTrackEvent('event status', 'started');
  }, []);

  React.useEffect(() => {
    if (verificationMethod === 'saml') {
      trackVoter({
        event: 'Participant authentication successful',
      });
    }
    trackVoter({
      event: 'Joined presentation',
      properties: getJoinedPresTrackProps({
        source,
        voteKey,
        participationIdentityMode,
      }),
    });
  }, [
    trackVoter,
    participationIdentityMode,
    source,
    verificationMethod,
    voteKey,
  ]);

  React.useEffect(() => {
    // We want to load the translation in case the user refreshes the page
    if (language) window.document.documentElement.lang = language;
  }, [language]);

  return (
    <NextIntlParticipatingProvider
      locale={language}
      messages={nextIntlMessages}
    >
      <Theme theme={theme}>
        <Box
          flexDirection="column"
          flex="1 0 auto"
          bg="bg"
          data-testid="voting-background"
        >
          <AudienceLimitReachedModal open={status === 'audienceLimitReached'} />

          <AuthenticationContainer>
            <IdentificationContainer>
              {paceMode === 'audience' ? (
                <AudiencePace voteKey={voteKey} />
              ) : (
                <PresenterPace voteKey={voteKey} />
              )}
            </IdentificationContainer>
          </AuthenticationContainer>
        </Box>
      </Theme>
    </NextIntlParticipatingProvider>
  );
};
